import { useBaseURL } from '@/shared/composables/url'

import { appBaseUrl, isDevelopment, isElectron, isNotDevelopment, tauriMode } from '../import-meta'

const APP_IMAGE_ORIGIN = 'http://appimage.localhost'

export const getFullURL = (path = '') => {
  if (path && path.startsWith('data:image')) {
    return path
  }

  if (path.startsWith('blob:')) {
    return path
  }

  if ((tauriMode() || isElectron()) && path) {
    return `${appBaseUrl()}${path}`
  } else {
    const { baseURL } = useBaseURL()
    return baseURL === '/' ? path : `${baseURL}${path}`
  }
}

export const getPathNameFromURL = (url: string) => {
  try {
    const { pathname } = new URL(url)
    return pathname
  } catch {
    return url
  }
}

export const getAbsoluteURL = (path: string) => {
  if (tauriMode() || isElectron() || isDevelopment()) {
    return `${appBaseUrl()}${path}`
  } else {
    const { baseURL } = useBaseURL()
    const resultPath = baseURL === '/' ? path : `${baseURL}${path}`
    return `${window.location.origin}${resultPath}`
  }
}

export const getAbsoluteWebSocketURL = (path: string) => {
  const absolute = getAbsoluteURL(path)
  if (/^https:\/\//i.test(absolute)) {
    return absolute.replace(/^https:\/\//i, 'wss://')
  }
  if (/^http:\/\//i.test(absolute)) {
    return absolute.replace(/^http:\/\//i, 'ws://')
  }
  // Legacy: relative/broken base — keep previous localhost heuristic for browser-only edge cases.
  const protocol = (!tauriMode() && isNotDevelopment() && location.hostname === 'localhost') ? 'ws://' : 'wss://'
  return absolute.replace(/http(s)?:\/\//i, protocol)
}

/**
 * Normalize integration-style values like `wss:host:5059` → `wss://host:5059`.
 */
const normalizeSipScheme = (value: string): string => {
  if (/^wss:\/\//i.test(value) || /^ws:\/\//i.test(value)) return value
  if (/^wss:/i.test(value)) return value.replace(/^wss:/i, 'wss://')
  if (/^ws:/i.test(value)) return value.replace(/^ws:/i, 'ws://')
  return value
}

/**
 * SIP WS URL for UA: absolute wss/ws as-is; http(s) → ws(s); path `/sip` or empty →
 * absolute via appBaseUrl (call after ensureAppBaseUrlReady in Tauri).
 * Other non-absolute strings are returned as-is (legacy / unexpected API values).
 */
export const resolveSipWebSocketURL = (
  value: string | null | undefined,
  fallbackPath: '/sip' | '/gsip' = '/sip',
): string => {
  const trimmed = value?.trim()
  // Registry / API may yield "False" when DOMAINADRESS or integration sipWs is unset.
  if (!trimmed || trimmed === 'False') {
    return getAbsoluteWebSocketURL(fallbackPath)
  }

  const withScheme = normalizeSipScheme(trimmed)
  if (/^wss?:\/\//i.test(withScheme)) {
    return withScheme
  }
  if (/^https?:\/\//i.test(withScheme)) {
    return withScheme.replace(/^https:\/\//i, 'wss://').replace(/^http:\/\//i, 'ws://')
  }
  if (withScheme.startsWith('/')) {
    return getAbsoluteWebSocketURL(withScheme)
  }
  // host:port from integration without scheme → assume wss (same as browser MSI expectation)
  if (/^[\w.-]+:\d+$/i.test(withScheme)) {
    return `wss://${withScheme}`
  }
  return withScheme
}

const isLocalOrDataUrl = (path: string) =>
  /^(blob:|data:)/i.test(path)

export const getAuthenticatedMediaUrl = (path = '') => {
  if (!path || isLocalOrDataUrl(path)) return path
  if (!tauriMode()) return getFullURL(path)

  const pathname = /^https?:/i.test(path)
    ? getPathNameFromURL(path)
    : (path.startsWith('/') ? path : `/${path}`)

  return `${APP_IMAGE_ORIGIN}${pathname}`
}
